import { DocumentNode, gql } from '@apollo/client';
import {
  Checkbox,
  Hide,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import * as React from 'react';

import { ResourceListItem } from '@/components/GroupPage/GroupResourcesPane/components/LibraryFileItem/components/ResourceListItem';
import { ResourceListFolder } from '@/components/GroupPage/GroupResourcesPane/components/LibraryFolderItem/ResourceListFolder/ResourceListFolder';
import { TableEmpty, TableLoading } from '@/components/Table';
import { Card } from '@/imports/ui/chakra/components/Card/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { ISelectionState } from '@/imports/ui/useSelectionState';

import {
  BrowseItemsList_libraryFolderResultsFragment_files_versionFile,
  BrowseItemsList_libraryFolderResultsFragment_folders,
} from './graphql-types/BrowseItemsList_libraryFolderResultsFragment';

type ILibraryFolder = BrowseItemsList_libraryFolderResultsFragment_folders;
type IVersionFile =
  BrowseItemsList_libraryFolderResultsFragment_files_versionFile;

type IBrowseItemsListProps = {
  folders: ILibraryFolder[];
  rootFolderId: string;
  versionFiles: IVersionFile[];
  selection: ISelectionState<{ _id: string }>;
  selectAll: () => void;
  selectNone: () => void;
  toggleOne: (item: { _id: string }) => void;
  navigateToFolder: (folderId: string) => void;
  currentUserCanEditGroupLibrary: boolean;
  groupId: string;
  loading: boolean;
};

export const BrowseItemsList: React.FC<IBrowseItemsListProps> & {
  libraryFolderResultsFragment: DocumentNode;
} = ({
  folders,
  rootFolderId,
  versionFiles,
  selection,
  selectAll,
  selectNone,
  toggleOne,
  navigateToFolder,
  currentUserCanEditGroupLibrary,
  groupId,
  loading,
}) => {
  return (
    <Stack>
      <Card overflow="hidden">
        <TableContainer>
          <Table size="sm" colorScheme="gray">
            <Thead bg="gray.100">
              <Tr>
                {currentUserCanEditGroupLibrary && (
                  <Th pr="0">
                    {versionFiles.length > 0 && (
                      <Checkbox
                        key="select-all"
                        isChecked={selection.allSelected}
                        isIndeterminate={
                          (!selection.allSelected &&
                            selection.includedItems.length > 0) ||
                          (selection.allSelected &&
                            selection.excludedItems.length > 0)
                        }
                        onChange={() =>
                          selection.allSelected ? selectNone() : selectAll()
                        }
                        boxSize={6}
                      />
                    )}
                  </Th>
                )}
                <Th>
                  <Text textStyle="detail">NAME</Text>
                </Th>
                <Hide below="lg">
                  <Th>
                    <Text textStyle="detail">DATE</Text>
                  </Th>
                  <Th pl="2">
                    <Text textStyle="detail">CREATED BY</Text>
                  </Th>
                </Hide>
                <Th />
              </Tr>
            </Thead>
            {loading ? (
              <TableLoading columns={4} />
            ) : folders.length || versionFiles.length ? (
              <Tbody>
                {folders.map((folder) => (
                  <ResourceListFolder
                    key={folder._id}
                    folder={folder}
                    rootFolderId={rootFolderId}
                    navigateToFolder={navigateToFolder}
                    onAfterMove={selectNone}
                    currentUserCanEditLibrary={currentUserCanEditGroupLibrary}
                  />
                ))}
                {versionFiles.map((versionFile) => (
                  <ResourceListItem
                    key={versionFile._id}
                    versionFile={versionFile}
                    selection={selection}
                    toggleOne={toggleOne}
                    canEditGroupLibrary={currentUserCanEditGroupLibrary}
                    groupId={groupId}
                    rootFolderId={rootFolderId}
                    onAfterMove={selectNone}
                    onAfterDelete={selectNone}
                  />
                ))}
              </Tbody>
            ) : (
              <TableEmpty>No files or folders in here yet.</TableEmpty>
            )}
          </Table>
        </TableContainer>
      </Card>
    </Stack>
  );
};

BrowseItemsList.libraryFolderResultsFragment = gql`
  fragment BrowseItemsList_libraryFolderResultsFragment on LibraryFolderContentsResult {
    folders {
      _id
      ...ResourceListFolder_LibraryFolderFragment
    }
    files {
      _id
      versionFile {
        ...ResourceListItem_VersionFileFragment
      }
    }
  }
  ${ResourceListFolder.folderFragment}
  ${ResourceListItem.versionFileFragment}
`;
